import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Shield, Tag, Check, X, Search, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccessTagsSelector({ 
  isOpen, 
  onClose, 
  user, // The user being edited/approved
  events = [],  // List of all events
  funds = [],   // List of all funds
  angelNetworks = [], // List of all angel networks
  investmentBankers = [], // List of all investment bankers
  onSave,       // Callback when tags are saved
  mode = 'approve' // 'approve' or 'edit'
}) {
  const [saving, setSaving] = useState(false);
  const [accessTags, setAccessTags] = useState({
    events: [],
    funds: [],
    angel_networks: [],
    investment_bankers: [],
    specific_deals: [],
    all_access: false
  });
  
  // Deals state for searchable multi-select
  const [allDeals, setAllDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealSearchQuery, setDealSearchQuery] = useState('');

  // Fetch all deals when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchAllDeals();
    }
  }, [isOpen]);

  const fetchAllDeals = async () => {
    setDealsLoading(true);
    try {
      const response = await axios.get(`${API}/deals`);
      setAllDeals(response.data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      setAllDeals([]);
    } finally {
      setDealsLoading(false);
    }
  };

  // Filter deals based on search query
  const filteredDeals = useMemo(() => {
    if (!dealSearchQuery.trim()) return allDeals;
    const query = dealSearchQuery.toLowerCase();
    return allDeals.filter(deal => 
      (deal.brand_name || deal.name || '').toLowerCase().includes(query) ||
      (deal.one_liner || deal.description || '').toLowerCase().includes(query)
    );
  }, [allDeals, dealSearchQuery]);

  // Initialize from user's existing access tags when editing
  useEffect(() => {
    if (isOpen && user) {
      if (user.access_tags) {
        setAccessTags({
          events: user.access_tags.events || [],
          funds: user.access_tags.funds || [],
          angel_networks: user.access_tags.angel_networks || [],
          investment_bankers: user.access_tags.investment_bankers || [],
          specific_deals: user.access_tags.specific_deals || [],
          all_access: user.access_tags.all_access || false
        });
      } else {
        // Reset to default for new users
        setAccessTags({
          events: [],
          funds: [],
          angel_networks: [],
          investment_bankers: [],
          specific_deals: [],
          all_access: false
        });
      }
      // Reset search query
      setDealSearchQuery('');
    }
  }, [isOpen, user]);

  const handleToggleAllAccess = (checked) => {
    setAccessTags({
      ...accessTags,
      all_access: checked
    });
  };

  const handleToggleItem = (category, item, checked) => {
    const currentItems = accessTags[category] || [];
    if (checked) {
      setAccessTags({
        ...accessTags,
        [category]: [...currentItems, item]
      });
    } else {
      setAccessTags({
        ...accessTags,
        [category]: currentItems.filter(i => i !== item)
      });
    }
  };

  // Handle toggling a specific deal
  const handleToggleDeal = (dealId, checked) => {
    const currentDeals = accessTags.specific_deals || [];
    if (checked) {
      setAccessTags({
        ...accessTags,
        specific_deals: [...currentDeals, dealId]
      });
    } else {
      setAccessTags({
        ...accessTags,
        specific_deals: currentDeals.filter(id => id !== dealId)
      });
    }
  };

  const handleSelectAll = (category, items) => {
    setAccessTags({
      ...accessTags,
      [category]: items.map(i => typeof i === 'string' ? i : i.name)
    });
  };

  const handleClearAll = (category) => {
    setAccessTags({
      ...accessTags,
      [category]: []
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(accessTags);
      onClose();
    } catch (error) {
      // Error handling done in parent
    } finally {
      setSaving(false);
    }
  };

  const getCategoryCount = (category, items) => {
    const selected = accessTags[category] || [];
    return `${selected.length}/${items.length}`;
  };

  // Render a category section with checkboxes
  const renderCategory = (title, category, items, getItemName = (item) => typeof item === 'string' ? item : item.name) => {
    const selectedItems = accessTags[category] || [];
    const itemNames = items.map(getItemName);
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-[#0F172A]">
            {title} <span className="text-[#64748B] font-normal">({getCategoryCount(category, items)})</span>
          </Label>
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={() => handleSelectAll(category, items)}
              className="text-xs text-brand h-6 px-2"
              disabled={accessTags.all_access}
            >
              Select All
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={() => handleClearAll(category)}
              className="text-xs text-red-500 h-6 px-2"
              disabled={accessTags.all_access}
            >
              Clear
            </Button>
          </div>
        </div>
        
        {items.length === 0 ? (
          <p className="text-sm text-[#64748B] italic">No {title.toLowerCase()} available</p>
        ) : (
          <ScrollArea className="h-32 border rounded-md p-2">
            <div className="space-y-2">
              {items.map((item, index) => {
                const itemName = getItemName(item);
                const isChecked = selectedItems.includes(itemName);
                return (
                  <div key={index} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${category}-${index}`}
                      checked={isChecked || accessTags.all_access}
                      disabled={accessTags.all_access}
                      onCheckedChange={(checked) => handleToggleItem(category, itemName, checked)}
                    />
                    <label
                      htmlFor={`${category}-${index}`}
                      className={`text-sm cursor-pointer ${accessTags.all_access ? 'text-gray-400' : 'text-[#0F172A]'}`}
                    >
                      {itemName}
                    </label>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    );
  };

  // Show summary of selected tags
  const renderSelectedSummary = () => {
    if (accessTags.all_access) {
      return (
        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <Shield className="h-5 w-5 text-green-600" />
          <span className="text-sm text-green-700 font-medium">Full Access - User can see all deals</span>
        </div>
      );
    }

    const totalTags = 
      (accessTags.events?.length || 0) + 
      (accessTags.funds?.length || 0) + 
      (accessTags.angel_networks?.length || 0) + 
      (accessTags.investment_bankers?.length || 0);
    
    const specificDealsCount = accessTags.specific_deals?.length || 0;

    if (totalTags === 0 && specificDealsCount === 0) {
      return (
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <X className="h-5 w-5 text-amber-600" />
          <span className="text-sm text-amber-700 font-medium">No tags or deals selected - User will have no access to deals</span>
        </div>
      );
    }

    // Get names of selected deals for display
    const selectedDealNames = allDeals
      .filter(d => accessTags.specific_deals?.includes(d.id))
      .map(d => d.brand_name || d.name);

    return (
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-700 font-medium">
            {totalTags > 0 && `${totalTags} tag(s)`}
            {totalTags > 0 && specificDealsCount > 0 && ' + '}
            {specificDealsCount > 0 && `${specificDealsCount} specific deal(s)`}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {accessTags.events?.map((e, i) => (
            <Badge key={`e-${i}`} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
              {e}
            </Badge>
          ))}
          {accessTags.funds?.map((f, i) => (
            <Badge key={`f-${i}`} variant="secondary" className="text-xs bg-blue-100 text-blue-700">
              {f}
            </Badge>
          ))}
          {accessTags.angel_networks?.map((a, i) => (
            <Badge key={`a-${i}`} variant="secondary" className="text-xs bg-green-100 text-green-700">
              {a}
            </Badge>
          ))}
          {accessTags.investment_bankers?.map((b, i) => (
            <Badge key={`b-${i}`} variant="secondary" className="text-xs bg-orange-100 text-orange-700">
              {b}
            </Badge>
          ))}
          {selectedDealNames.map((name, i) => (
            <Badge key={`d-${i}`} variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">
              <Building2 className="h-3 w-3 mr-1" />
              {name}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand" />
            {mode === 'approve' ? 'Approve User & Set Access' : 'Edit User Access'}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {user && (
              <span className="font-medium text-[#0F172A]">
                {user.first_name} {user.last_name} ({user.email})
              </span>
            )}
            <br />
            Configure which deals this user can access based on tags.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* All Access Toggle */}
          <div className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-brand" />
              <div>
                <Label className="text-sm font-semibold text-[#0F172A]">Grant Full Access</Label>
                <p className="text-xs text-[#64748B]">User will be able to see all deals regardless of tags</p>
              </div>
            </div>
            <Switch
              checked={accessTags.all_access}
              onCheckedChange={handleToggleAllAccess}
              className="data-[state=checked]:bg-[#10B981]"
            />
          </div>

          {/* Selected Summary */}
          {renderSelectedSummary()}

          {/* Tag Categories */}
          <div className={`grid grid-cols-2 gap-4 ${accessTags.all_access ? 'opacity-50' : ''}`}>
            {renderCategory('Events', 'events', events)}
            {renderCategory('Funds', 'funds', funds, (f) => f.name || f)}
            {renderCategory('Angel Networks', 'angel_networks', angelNetworks, (a) => a.name || a)}
            {renderCategory('Investment Bankers', 'investment_bankers', investmentBankers, (b) => b.name || b)}
          </div>

          {/* Specific Deals Selection - Searchable Multi-Select */}
          <div className={`space-y-3 ${accessTags.all_access ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-600" />
                <Label className="text-sm font-semibold text-[#0F172A]">
                  Specific Deals <span className="text-[#64748B] font-normal">({accessTags.specific_deals?.length || 0}/{allDeals.length})</span>
                </Label>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAccessTags({...accessTags, specific_deals: allDeals.map(d => d.id)})}
                  className="text-xs text-brand h-6 px-2"
                  disabled={accessTags.all_access}
                >
                  Select All
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAccessTags({...accessTags, specific_deals: []})}
                  className="text-xs text-red-500 h-6 px-2"
                  disabled={accessTags.all_access}
                >
                  Clear
                </Button>
              </div>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
              <Input
                placeholder="Search deals by name..."
                value={dealSearchQuery}
                onChange={(e) => setDealSearchQuery(e.target.value)}
                className="pl-10 h-9 text-sm"
                disabled={accessTags.all_access}
              />
            </div>
            
            {/* Deals List */}
            {dealsLoading ? (
              <div className="flex items-center justify-center h-32 border rounded-md">
                <Loader2 className="h-5 w-5 animate-spin text-[#64748B]" />
                <span className="ml-2 text-sm text-[#64748B]">Loading deals...</span>
              </div>
            ) : filteredDeals.length === 0 ? (
              <div className="flex items-center justify-center h-32 border rounded-md">
                <span className="text-sm text-[#64748B]">
                  {dealSearchQuery ? 'No deals match your search' : 'No deals available'}
                </span>
              </div>
            ) : (
              <ScrollArea className="h-48 border rounded-md p-2">
                <div className="space-y-2">
                  {filteredDeals.map((deal) => {
                    const isChecked = accessTags.specific_deals?.includes(deal.id);
                    const dealName = deal.brand_name || deal.name || 'Unnamed Deal';
                    const dealOneLiner = deal.one_liner || deal.description || '';
                    return (
                      <div 
                        key={deal.id} 
                        className={`flex items-start space-x-2 p-2 rounded-md hover:bg-gray-50 ${isChecked ? 'bg-indigo-50' : ''}`}
                      >
                        <Checkbox
                          id={`deal-${deal.id}`}
                          checked={isChecked || accessTags.all_access}
                          disabled={accessTags.all_access}
                          onCheckedChange={(checked) => handleToggleDeal(deal.id, checked)}
                          className="mt-0.5"
                        />
                        <label
                          htmlFor={`deal-${deal.id}`}
                          className={`text-sm cursor-pointer flex-1 ${accessTags.all_access ? 'text-gray-400' : 'text-[#0F172A]'}`}
                        >
                          <span className="font-medium">{dealName}</span>
                          {dealOneLiner && (
                            <span className="block text-xs text-[#64748B] truncate max-w-xs">
                              {dealOneLiner.substring(0, 60)}{dealOneLiner.length > 60 ? '...' : ''}
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {mode === 'approve' ? 'Approving...' : 'Saving...'}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {mode === 'approve' ? 'Approve User' : 'Save Changes'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export helper to display current access tags
export function AccessTagsBadges({ accessTags }) {
  if (!accessTags) return null;
  
  if (accessTags.all_access) {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-300">
        <Shield className="h-3 w-3 mr-1" />
        Full Access
      </Badge>
    );
  }

  const totalTags = 
    (accessTags.events?.length || 0) + 
    (accessTags.funds?.length || 0) + 
    (accessTags.angel_networks?.length || 0) + 
    (accessTags.investment_bankers?.length || 0);
  
  const specificDealsCount = accessTags.specific_deals?.length || 0;

  if (totalTags === 0 && specificDealsCount === 0) {
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-300">
        <X className="h-3 w-3 mr-1" />
        No Access
      </Badge>
    );
  }

  // Build display text
  let displayText = '';
  if (totalTags > 0) displayText += `${totalTags} tag(s)`;
  if (totalTags > 0 && specificDealsCount > 0) displayText += ' + ';
  if (specificDealsCount > 0) displayText += `${specificDealsCount} deal(s)`;

  return (
    <Badge className="bg-blue-100 text-blue-700 border-blue-300">
      <Tag className="h-3 w-3 mr-1" />
      {displayText}
    </Badge>
  );
}
